import { useState, useEffect } from "react";

// Inline SVG placeholder shown when a product image is missing or fails to
// load. Kept as a data URI so we don't add a new asset to the bundle and so
// styled-component consumers can use it as a background-image if they want.
const PLACEHOLDER_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" role="img" aria-label="no image">
       <rect width="120" height="120" fill="#F4F5F5"/>
       <g fill="none" stroke="#BDBFCE" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
         <rect x="22" y="30" width="76" height="60" rx="6"/>
         <circle cx="42" cy="52" r="6"/>
         <path d="M26 82 L52 58 L72 78 L86 66 L94 74"/>
       </g>
     </svg>`
  );

/**
 * ProductImage renders an <img> for a product thumbnail/photo and transparently
 * falls back to an inline SVG placeholder when:
 *   1. `src` is empty / undefined, or
 *   2. the image fails to load (network error, 404, CORS, etc.)
 *
 * It accepts the usual <img> props and forwards `className` + `style`, so it
 * drops into existing `styled.img(...)` components via `styled(ProductImage)(...)`
 * without changing the layout.
 */
const ProductImage = ({
  src,
  alt = "",
  className,
  style,
  onClick,
  width,
  height,
  ...rest
}) => {
  const [errored, setErrored] = useState(false);

  // Reset the error flag whenever the src changes, so a new product's
  // (valid) image gets a chance to load even if a previous one failed.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const showPlaceholder = !src || errored;

  return (
    <img
      {...rest}
      src={showPlaceholder ? PLACEHOLDER_DATA_URI : src}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      width={width}
      height={height}
      onError={() => {
        if (!errored) setErrored(true);
      }}
      // Disable the browser's native drag ghost on the broken-image glyph
      // that briefly appears between the error and the state update.
      draggable={false}
    />
  );
};

export default ProductImage;
export { PLACEHOLDER_DATA_URI };
